// Build timestamp: 1776007029
// Updated: 1776008649
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { toast, Toaster } from 'sonner';
import {
  LayoutDashboard, Users, MapPin, Briefcase, Calendar, ClipboardList,
  FileText, AlertCircle, DollarSign, TrendingUp, CheckCircle, XCircle,
  Menu, LogOut, Plus, Eye, CheckSquare, Clock, Building, Phone, Mail,
  Settings, Edit, Trash2, Bell
} from 'lucide-react';
import { NotificationsDropdown } from './components/NotificationsDropdown';
import { CalendarPage } from './components/CalendarPage';
import { IssuesPage } from './components/IssuesPage';

import { EnhancedBiddingPage } from './EnhancedBiddingPage';
import { ProposalsPage } from './ProposalsPage';
import { SettingsPage } from './SettingsPage';
import { EnhancedLeadForm } from './components/EnhancedLeadForm';
import { MultiStepWalkthroughForm } from './components/MultiStepWalkthroughForm';
import { ZoneTaskMatrixEditor } from './components/ZoneTaskMatrixEditor';
import { RecordContextHeader, getStatusLabel } from './components/RecordContextHeader';
import { EmptyState } from './components/EmptyState';
import { NextStepPrompt } from './components/NextStepPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth functions
const getToken = () => localStorage.getItem('token');
const getUser = () => JSON.parse(localStorage.getItem('user') || '{}');
const setAuth = (token, user) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
};
const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// API helper
const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    // Clone response before reading to avoid "body already read" error
    const responseClone = response.clone();
    let errorText = 'Request failed';
    try {
      errorText = await responseClone.text();
    } catch (e) {
      console.error('Failed to read error response:', e);
    }
    const error = new Error(errorText);
    error.status = response.status;
    error.statusText = response.statusText;
    throw error;
  }
  
  return response.json();
};

// Login Component
function Login({ onLogin }) {
  const [email, setEmail] = useState('admin@tidify.com');
  const [password, setPassword] = useState('admin123');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      setAuth(data.token, data.user);
      onLogin(data.user);
      toast.success('Logged in successfully');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-emerald-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-[Space_Grotesk] text-brand">Tidify</CardTitle>
          <CardDescription>Janitorial Bidding, Operations, and Growth</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-button">
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Demo: admin@tidify.com / admin123
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Dashboard Component
function Dashboard({ setCurrentPage }) {
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [proposals, setProposals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsData, leadsData, workOrdersData, walkthroughsData, proposalsData] = await Promise.allSettled([
        api('/api/dashboard/stats'),
        api('/api/leads'),
        api('/api/work-orders'),
        api('/api/walkthroughs'),
        api('/api/proposals')
      ]);
      
      // Extract values, defaulting to empty/safe values if any call failed
      setStats(statsData.status === 'fulfilled' ? statsData.value : {});
      setLeads(leadsData.status === 'fulfilled' ? leadsData.value.slice(0, 5) : []);
      setWorkOrders(workOrdersData.status === 'fulfilled' ? workOrdersData.value.slice(0, 5) : []);
      setWalkthroughs(walkthroughsData.status === 'fulfilled' ? walkthroughsData.value.slice(0, 5) : []);
      setProposals(proposalsData.status === 'fulfilled' ? proposalsData.value.filter(p => p.status === 'draft' || p.status === 'sent').slice(0, 5) : []);
      
      // Only show error if ALL calls failed
      const allFailed = [statsData, leadsData, workOrdersData, walkthroughsData, proposalsData].every(r => r.status === 'rejected');
      if (allFailed) {
        toast.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to Tidify</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="kpi-card cursor-pointer" data-testid="dashboard-today-jobs-card" onClick={() => setCurrentPage('work-orders')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Jobs</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{stats.today_jobs || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Click to view work orders</p>
          </CardContent>
        </Card>

        <Card className="kpi-card cursor-pointer" data-testid="dashboard-completed-card" onClick={() => setCurrentPage('work-orders')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums text-success">{stats.completed_jobs || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Click to view work orders</p>
          </CardContent>
        </Card>

        <Card className="kpi-card cursor-pointer" data-testid="dashboard-clients-card" onClick={() => setCurrentPage('clients')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{stats.active_clients || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Click to view clients</p>
          </CardContent>
        </Card>

        <Card className="kpi-card cursor-pointer" data-testid="dashboard-issues-card" onClick={() => setCurrentPage('issues')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Issues</CardTitle>
            <AlertCircle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold tabular-nums">{stats.open_issues || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Issues tracking (coming soon)</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Upcoming Walkthroughs</CardTitle>
            <CardDescription>Recently created site walkthroughs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {walkthroughs.map((wt) => {
                const lead = leads.find(l => l.id === wt.lead_id);
                return (
                  <div 
                    key={wt.id} 
                    className="flex justify-between items-center cursor-pointer hover:bg-secondary p-2 rounded-lg transition-colors"
                    onClick={() => setCurrentPage('bidding')}
                    data-testid="dashboard-walkthrough-item"
                  >
                    <div>
                      <p className="font-medium text-sm">{lead?.company_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{wt.square_footage} sq ft · {wt.building_type?.replace('_', ' ')}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{wt.frequency}</Badge>
                  </div>
                );
              })}
              {walkthroughs.length === 0 && <p className="text-muted-foreground text-sm">No walkthroughs yet</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Proposals Needing Action</CardTitle>
            <CardDescription>Draft or sent proposals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {proposals.map((proposal) => (
                <div 
                  key={proposal.id} 
                  className="flex justify-between items-center cursor-pointer hover:bg-secondary p-2 rounded-lg transition-colors"
                  onClick={() => setCurrentPage('proposals')}
                  data-testid="dashboard-proposal-item"
                >
                  <div>
                    <p className="font-medium text-sm">{proposal.company_name}</p>
                    <p className="text-xs text-muted-foreground">${proposal.monthly_price}/mo</p>
                  </div>
                  <Badge className={`status-${proposal.status} text-xs`}>{getStatusLabel(proposal.status)}</Badge>
                </div>
              ))}
              {proposals.length === 0 && <p className="text-muted-foreground text-sm">No proposals needing action</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leads.map((lead) => (
                <div 
                  key={lead.id} 
                  className="flex justify-between items-center cursor-pointer hover:bg-secondary p-2 rounded-lg transition-colors"
                  onClick={() => setCurrentPage('leads')}
                  data-testid="dashboard-lead-item"
                >
                  <div>
                    <p className="font-medium text-sm">{lead.company_name}</p>
                    <p className="text-xs text-muted-foreground">{lead.contact_name}</p>
                  </div>
                  <Badge className={`status-${lead.status} text-xs`}>{getStatusLabel(lead.status)}</Badge>
                </div>
              ))}
              {leads.length === 0 && <p className="text-muted-foreground text-sm">No leads yet</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Work Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {workOrders.map((wo) => (
                <div 
                  key={wo.id} 
                  className="flex justify-between items-center cursor-pointer hover:bg-secondary p-2 rounded-lg transition-colors"
                  onClick={() => setCurrentPage('work-orders')}
                  data-testid="dashboard-workorder-item"
                >
                  <div>
                    <p className="font-medium text-sm">{wo.date}</p>
                    <p className="text-xs text-muted-foreground">Assigned: {wo.assigned_cleaner}</p>
                  </div>
                  <Badge className={`status-${wo.status} text-xs`}>{getStatusLabel(wo.status)}</Badge>
                </div>
              ))}
              {workOrders.length === 0 && <p className="text-muted-foreground text-sm">No work orders yet</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Leads Component
function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);

  useEffect(() => {
    loadLeads();
  }, []);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedLeadId) {
      const timer = setTimeout(() => setHighlightedLeadId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedLeadId]);

  const loadLeads = async () => {
    try {
      const data = await api('/api/leads');
      setLeads(data);
    } catch (error) {
      toast.error('Failed to load leads');
    }
  };

  const handleSaveLead = (leadId, action) => {
    setShowForm(false);
    setEditingLead(null);
    loadLeads().then(() => {
      // Highlight the saved lead
      if (leadId) {
        setHighlightedLeadId(leadId);
        // Scroll to the lead
        setTimeout(() => {
          const element = document.querySelector(`[data-lead-id="${leadId}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    });
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Leads & Pipeline</h1>
          <p className="text-muted-foreground">Manage your sales pipeline</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditingLead(null); }} data-testid="new-lead-button">
          <Plus className="h-4 w-4 mr-2" />
          New Lead
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <EnhancedLeadForm
              leadId={editingLead?.id}
              onSave={handleSaveLead}
              onCancel={() => { setShowForm(false); setEditingLead(null); }}
            />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="leads-tab-all">All Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="lead" data-testid="leads-tab-new">New ({leads.filter(l => l.status === 'lead').length})</TabsTrigger>
          <TabsTrigger value="contacted" data-testid="leads-tab-contacted">Contacted ({leads.filter(l => l.status === 'contacted').length})</TabsTrigger>
          <TabsTrigger value="proposal_sent" data-testid="leads-tab-proposal">Proposal Sent ({leads.filter(l => l.status === 'proposal_sent').length})</TabsTrigger>
          <TabsTrigger value="won" data-testid="leads-tab-won">Won ({leads.filter(l => l.status === 'won').length})</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="mt-6">
          <LeadsList leads={leads} onEdit={handleEditLead} onNewLead={() => { setShowForm(true); setEditingLead(null); }} />
        </TabsContent>

        <TabsContent value="lead" className="mt-6">
          <LeadsList leads={leads.filter(l => l.status === 'lead')} emptyMessage="No new leads yet" onEdit={handleEditLead} onNewLead={() => { setShowForm(true); setEditingLead(null); }} />
        </TabsContent>

        <TabsContent value="contacted" className="mt-6">
          <LeadsList leads={leads.filter(l => l.status === 'contacted')} emptyMessage="No contacted leads yet" onEdit={handleEditLead} onNewLead={() => { setShowForm(true); setEditingLead(null); }} />
        </TabsContent>

        <TabsContent value="proposal_sent" className="mt-6">
          <LeadsList leads={leads.filter(l => l.status === 'proposal_sent')} emptyMessage="No proposals sent yet" onEdit={handleEditLead} onNewLead={() => { setShowForm(true); setEditingLead(null); }} />
        </TabsContent>

        <TabsContent value="won" className="mt-6">
          <LeadsList leads={leads.filter(l => l.status === 'won')} emptyMessage="No won leads yet" onEdit={handleEditLead} onNewLead={() => { setShowForm(true); setEditingLead(null); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// LeadsList Component
function LeadsList({ leads, emptyMessage = "No leads yet", onEdit, onNewLead, highlightedLeadId }) {
  if (leads.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title={emptyMessage}
        message="Get started by creating your first lead to begin tracking potential clients."
        actionLabel="Create Lead"
        onActionClick={onNewLead}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {leads.map((lead) => {
        const isHighlighted = lead.id === highlightedLeadId;
        return (
          <Card 
            key={lead.id} 
            className={`hover:shadow-md transition-all ${isHighlighted ? 'ring-2 ring-primary shadow-lg' : ''}`}
            data-testid="lead-card"
            data-lead-id={lead.id}
          >
            <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{lead.company_name}</h3>
                <p className="text-sm text-muted-foreground">{lead.contact_name}</p>
                {lead.facility_name && (
                  <p className="text-sm text-muted-foreground mt-1">
                    <Building className="h-3 w-3 inline mr-1" />
                    {lead.facility_name}
                  </p>
                )}
                <div className="flex gap-4 mt-2 text-sm">
                  {lead.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {lead.phone}
                    </span>
                  )}
                  {lead.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {lead.email}
                    </span>
                  )}
                </div>
                {lead.updated_at && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Last updated: {new Date(lead.updated_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge className={`status-${lead.status}`} data-testid="lead-status-badge">{getStatusLabel(lead.status)}</Badge>
                {onEdit && (
                  <Button variant="outline" size="sm" onClick={() => onEdit(lead)} data-testid="edit-lead-button">
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )})}
    </div>
  );
}

// Continue in part 2...

// Bidding Component
function BiddingPage() {
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [bids, setBids] = useState([]);
  const [showWalkthroughForm, setShowWalkthroughForm] = useState(false);
  const [leads, setLeads] = useState([]);
  const [selectedWalkthrough, setSelectedWalkthrough] = useState(null);
  const [walkthroughForm, setWalkthroughForm] = useState({
    lead_id: '',
    square_footage: '',
    floors: 1,
    building_type: 'standard_office',
    num_restrooms: '',
    num_breakrooms: 0,
    floor_types: 'mixed',
    frequency: 'weekly',
    traffic_level: 'normal',
    condition: 'good',
    special_requirements: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leadsData, walkthroughsData, bidsData] = await Promise.all([
        api('/api/leads'),
        api('/api/walkthroughs'),
        api('/api/bids')
      ]);
      setLeads(leadsData);
      setWalkthroughs(walkthroughsData);
      setBids(bidsData);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleWalkthroughSubmit = async (e) => {
    e.preventDefault();
    try {
      await api('/api/walkthroughs', {
        method: 'POST',
        body: JSON.stringify(walkthroughForm)
      });
      toast.success('Walkthrough created');
      setShowWalkthroughForm(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create walkthrough');
    }
  };

  const handleCalculateBid = async (walkthroughId) => {
    try {
      const bidData = await api('/api/bids/calculate', {
        method: 'POST',
        body: JSON.stringify({
          walkthrough_id: walkthroughId,
          supplies_included: true
        })
      });
      toast.success('Bid calculated successfully');
      loadData();
      // Show the bid result
      const bid = bids.find(b => b.id === bidData.id) || bidData;
      alert(`Recommended Monthly Price: $${bidData.summary.recommended_monthly_price}
Price Range: ${bidData.summary.price_range}
Estimated Hours: ${bidData.summary.estimated_monthly_hours}`);
    } catch (error) {
      toast.error('Failed to calculate bid');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Bidding & Walkthroughs</h1>
          <p className="text-muted-foreground">Create walkthroughs and generate bids</p>
        </div>
        <Button onClick={() => setShowWalkthroughForm(!showWalkthroughForm)} data-testid="new-walkthrough-button">
          <Plus className="h-4 w-4 mr-2" />
          New Walkthrough
        </Button>
      </div>

      {showWalkthroughForm && (
        <Card>
          <CardHeader>
            <CardTitle>New Walkthrough</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleWalkthroughSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Lead *</Label>
                <Select value={walkthroughForm.lead_id} onValueChange={(value) => setWalkthroughForm({...walkthroughForm, lead_id: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leads.map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>{lead.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="square_footage">Square Footage *</Label>
                <Input
                  id="square_footage"
                  type="number"
                  value={walkthroughForm.square_footage}
                  onChange={(e) => setWalkthroughForm({...walkthroughForm, square_footage: e.target.value})}
                  required
                  data-testid="walkthrough-sqft-input"
                />
              </div>
              <div>
                <Label>Building Type</Label>
                <Select value={walkthroughForm.building_type} onValueChange={(value) => setWalkthroughForm({...walkthroughForm, building_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light_office">Light Office</SelectItem>
                    <SelectItem value="standard_office">Standard Office</SelectItem>
                    <SelectItem value="medical">Medical/Dental</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="num_restrooms">Number of Restrooms *</Label>
                <Input
                  id="num_restrooms"
                  type="number"
                  value={walkthroughForm.num_restrooms}
                  onChange={(e) => setWalkthroughForm({...walkthroughForm, num_restrooms: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="num_breakrooms">Number of Breakrooms</Label>
                <Input
                  id="num_breakrooms"
                  type="number"
                  value={walkthroughForm.num_breakrooms}
                  onChange={(e) => setWalkthroughForm({...walkthroughForm, num_breakrooms: e.target.value})}
                />
              </div>
              <div>
                <Label>Floor Types</Label>
                <Select value={walkthroughForm.floor_types} onValueChange={(value) => setWalkthroughForm({...walkthroughForm, floor_types: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carpet">Carpet</SelectItem>
                    <SelectItem value="hard_floor">Hard Floor</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                    <SelectItem value="specialty">Specialty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cleaning Frequency</Label>
                <Select value={walkthroughForm.frequency} onValueChange={(value) => setWalkthroughForm({...walkthroughForm, frequency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (5x/week)</SelectItem>
                    <SelectItem value="3x_week">3x per week</SelectItem>
                    <SelectItem value="2x_week">2x per week</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Condition</Label>
                <Select value={walkthroughForm.condition} onValueChange={(value) => setWalkthroughForm({...walkthroughForm, condition: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={walkthroughForm.notes}
                  onChange={(e) => setWalkthroughForm({...walkthroughForm, notes: e.target.value})}
                />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button type="submit" data-testid="save-walkthrough-button">Save Walkthrough</Button>
                <Button type="button" variant="outline" onClick={() => setShowWalkthroughForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-semibold mb-4">Walkthroughs</h2>
          <div className="space-y-3">
            {walkthroughs.map((wt) => (
              <Card key={wt.id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{wt.square_footage} sq ft - {wt.building_type.replace('_', ' ')}</p>
                      <p className="text-sm text-muted-foreground">
                        {wt.frequency} | {wt.num_restrooms} restrooms
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleCalculateBid(wt.id)}
                      data-testid="calculate-bid-button"
                    >
                      Calculate Bid
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {walkthroughs.length === 0 && <p className="text-muted-foreground">No walkthroughs yet</p>}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold mb-4">Generated Bids</h2>
          <div className="space-y-3">
            {bids.map((bid) => (
              <Card key={bid.id} className="bg-gradient-to-br from-teal-50 to-emerald-50">
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <p className="font-semibold text-lg">
                        ${bid.summary?.recommended_monthly_price || bid.pricing?.recommended_price}/mo
                      </p>
                      <Badge variant="outline" className="bg-white">
                        {bid.summary?.profit_margin_pct || bid.pricing?.mid_markup_pct}% margin
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Range: {bid.summary?.price_range || `$${bid.pricing?.low_price} - $${bid.pricing?.high_price}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Est. hours: {bid.summary?.estimated_monthly_hours || 0}/mo
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
            {bids.length === 0 && <p className="text-muted-foreground">No bids generated yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Clients Page
function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [linkedProposals, setLinkedProposals] = useState([]);
  const [linkedWorkOrders, setLinkedWorkOrders] = useState([]);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient && !showEditForm) {
      loadLinkedData();
    }
  }, [selectedClient, showEditForm]);

  const loadClients = async () => {
    try {
      const data = await api('/api/clients');
      setClients(data);
    } catch (error) {
      toast.error('Failed to load clients');
    }
  };

  const loadLinkedData = async () => {
    if (!selectedClient) return;
    try {
      const [proposalsData, workOrdersData] = await Promise.all([
        api(`/api/proposals?client_id=${selectedClient.id}`),
        api(`/api/work-orders?client_id=${selectedClient.id}`)
      ]);
      setLinkedProposals(proposalsData || []);
      setLinkedWorkOrders(workOrdersData || []);
    } catch (error) {
      // Silent fail for linked data
      setLinkedProposals([]);
      setLinkedWorkOrders([]);
    }
  };

  const handleView = (client) => {
    setSelectedClient(client);
    setShowEditForm(false);
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditFormData(client);
    setShowEditForm(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedClient) return;
    try {
      await api(`/api/clients/${selectedClient.id}`, {
        method: 'PUT',
        body: JSON.stringify(editFormData)
      });
      toast.success('Client updated');
      setShowEditForm(false);
      setSelectedClient(null);
      loadClients();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Delete this client? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Read error response
        const errorText = await response.text();
        let errorMessage = 'Failed to delete client';
        
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // If not JSON, use raw text if it contains useful info
          if (errorText && errorText.length > 0 && errorText.length < 200) {
            errorMessage = errorText;
          }
        }
        
        toast.error(errorMessage);
        return;
      }

      // Success
      toast.success('Client deleted successfully');
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
        setShowEditForm(false);
      }
      loadClients();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete client. Please try again.');
    }
  };

  if (selectedClient && showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Edit Client</h1>
          <Button variant="outline" onClick={() => { setShowEditForm(false); setSelectedClient(null); }}>
            Cancel
          </Button>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={editFormData.company_name || ''}
                  onChange={(e) => setEditFormData({...editFormData, company_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={editFormData.contact_name || ''}
                  onChange={(e) => setEditFormData({...editFormData, contact_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editFormData.phone || ''}
                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Input
                  value={editFormData.address || ''}
                  onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                />
              </div>
              <div className="md:col-span-2 flex gap-2 pt-4">
                <Button onClick={handleSaveEdit} data-testid="save-client-button">Save Changes</Button>
                <Button variant="outline" onClick={() => { setShowEditForm(false); setSelectedClient(null); }}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (selectedClient && !showEditForm) {
    return (
      <div className="space-y-6">
        {/* Context Header */}
        <RecordContextHeader
          label="Client Record"
          company={selectedClient.company_name}
          facility={selectedClient.address || 'No address'}
          status="active"
          additionalInfo={selectedClient.monthly_price ? `$${selectedClient.monthly_price}/mo` : ''}
        />

        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Client Details</h1>
          <Button variant="outline" onClick={() => setSelectedClient(null)}>
            ← Back to List
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-2xl">{selectedClient.company_name}</CardTitle>
              <CardDescription>{selectedClient.contact_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    {selectedClient.phone}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedClient.email}
                  </p>
                </div>
                {selectedClient.address && (
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {selectedClient.address}
                    </p>
                  </div>
                )}
                {selectedClient.monthly_price && (
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Service Fee</p>
                    <p className="font-semibold text-lg text-brand tabular-nums">
                      ${selectedClient.monthly_price}
                    </p>
                  </div>
                )}
                {selectedClient.frequency && (
                  <div>
                    <p className="text-sm text-muted-foreground">Service Frequency</p>
                    <p className="font-medium">{selectedClient.frequency.replace('_', ' ')}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={() => handleEdit(selectedClient)} data-testid="edit-client-button">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => handleDelete(selectedClient.id)} data-testid="delete-client-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Linked Data */}
          <div className="lg:col-span-2 space-y-6">
            {/* Linked Proposals */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Linked Proposals</CardTitle>
              </CardHeader>
              <CardContent>
                {linkedProposals.length === 0 && (
                  <p className="text-sm text-muted-foreground">No proposals linked to this client</p>
                )}
                <div className="space-y-2">
                  {linkedProposals.map((proposal) => (
                    <div key={proposal.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-secondary" data-testid="linked-proposal">
                      <div>
                        <p className="font-medium text-sm">${proposal.monthly_price}/mo</p>
                        <p className="text-xs text-muted-foreground">
                          {getStatusLabel(proposal.status)} • Created {new Date(proposal.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => window.alert('Open proposal feature coming soon')} data-testid="open-linked-proposal-button">
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Linked Work Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Work Orders</CardTitle>
              </CardHeader>
              <CardContent>
                {linkedWorkOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No work orders for this client</p>
                )}
                <div className="space-y-2">
                  {linkedWorkOrders.slice(0, 5).map((wo) => (
                    <div key={wo.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-secondary" data-testid="linked-work-order">
                      <div>
                        <p className="font-medium text-sm">{wo.date}</p>
                        <p className="text-xs text-muted-foreground">
                          {getStatusLabel(wo.status)} • Assigned: {wo.assigned_cleaner || 'Unassigned'}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => window.alert('Open work order feature coming soon')} data-testid="open-linked-workorder-button">
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Clients</h1>
        <p className="text-muted-foreground">Manage your active clients</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((client) => (
          <Card key={client.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-lg">{client.company_name}</CardTitle>
              <CardDescription>{client.contact_name}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="space-y-1 text-sm">
                  <p className="flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    {client.phone}
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {client.email}
                  </p>
                  {client.monthly_price && (
                    <p className="flex items-center gap-2 text-brand font-semibold">
                      <DollarSign className="h-3 w-3" />
                      ${client.monthly_price}/mo
                    </p>
                  )}
                  {client.frequency && (
                    <p className="text-xs text-muted-foreground">
                      Service: {client.frequency.replace('_', ' ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(client)} data-testid="view-client-button">
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleEdit(client)}>
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {clients.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No clients yet. Convert leads to clients by marking proposals as won!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Work Orders Page
function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState([]);
  const user = getUser();

  useEffect(() => {
    loadWorkOrders();
  }, []);

  const loadWorkOrders = async () => {
    try {
      const data = await api('/api/work-orders');
      setWorkOrders(data);
    } catch (error) {
      toast.error('Failed to load work orders');
    }
  };

  const handleCheckIn = async (workOrderId) => {
    try {
      await api(`/api/work-orders/${workOrderId}/check-in`, {
        method: 'POST',
        body: JSON.stringify({ location: { lat: 0, lng: 0 } })
      });
      toast.success('Checked in successfully');
      loadWorkOrders();
    } catch (error) {
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async (workOrderId) => {
    try {
      await api(`/api/work-orders/${workOrderId}/check-out`, {
        method: 'POST',
        body: JSON.stringify({ location: { lat: 0, lng: 0 } })
      });
      toast.success('Checked out successfully');
      loadWorkOrders();
    } catch (error) {
      toast.error('Failed to check out');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Work Orders</h1>
        <p className="text-muted-foreground">Manage cleaning jobs</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {workOrders.map((wo) => (
          <Card key={wo.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold">{wo.date}</h3>
                    <Badge className={`status-${wo.status}`}>{getStatusLabel(wo.status)}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">Location: {wo.location_id}</p>
                  <p className="text-sm text-muted-foreground">Assigned: {wo.assigned_cleaner}</p>
                  {wo.check_in_time && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Check-in: {new Date(wo.check_in_time).toLocaleTimeString()}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {wo.status === 'scheduled' && (
                    <Button size="sm" onClick={() => handleCheckIn(wo.id)} data-testid="check-in-button">
                      <CheckSquare className="h-4 w-4 mr-1" />
                      Check In
                    </Button>
                  )}
                  {wo.status === 'in_progress' && (
                    <Button size="sm" onClick={() => handleCheckOut(wo.id)} data-testid="check-out-button">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Check Out
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {workOrders.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">No work orders yet</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Main App Component
function AppLayout() {
  const [user, setUser] = useState(getUser());
  const [currentPage, setCurrentPage] = useState('dashboard');
  const navigate = useNavigate();

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: TrendingUp },
    { id: 'bidding', label: 'Bidding', icon: FileText },
    { id: 'proposals', label: 'Proposals', icon: FileText },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'work-orders', label: 'Work Orders', icon: ClipboardList },
    { id: 'issues', label: 'Issues', icon: AlertCircle },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-0">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: Two-row layout */}
          <div className="md:hidden">
            {/* Row 1: Brand + User */}
            <div className="flex justify-between items-center h-14 border-b">
              <h1 className="text-xl font-[Space_Grotesk] font-bold text-brand" data-testid="mobile-brand-logo">Tidify</h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{user.name}</span>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="mobile-logout-button">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* Row 2: Scrollable nav */}
            <div className="overflow-x-auto scrollbar-hide">
              <nav className="flex gap-1 py-2 min-w-max px-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setCurrentPage(item.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      currentPage === item.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-secondary'
                    }`}
                    data-testid={`mobile-nav-${item.id}-button`}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Desktop: Single-row layout */}
          <div className="hidden md:flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <h1 className="text-2xl font-[Space_Grotesk] font-bold text-brand" data-testid="desktop-brand-logo">Tidify</h1>
              <nav className="flex gap-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setCurrentPage(item.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === item.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-secondary'
                    }`}
                    data-testid={`nav-${item.id}-button`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <NotificationsDropdown />
              <span className="text-sm text-muted-foreground">{user.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {currentPage === 'dashboard' && <Dashboard setCurrentPage={setCurrentPage} />}
        {currentPage === 'leads' && <LeadsPage />}
        {currentPage === 'bidding' && <EnhancedBiddingPage />}
        {currentPage === 'proposals' && <ProposalsPage />}
        {currentPage === 'clients' && <ClientsPage />}
        {currentPage === 'calendar' && <CalendarPage />}
        {currentPage === 'work-orders' && <WorkOrdersPage />}
        {currentPage === 'issues' && <IssuesPage />}
        {currentPage === 'settings' && <SettingsPage />}
      </main>
    </div>
  );
}

// Main App
function App() {
  const [authenticated, setAuthenticated] = useState(!!getToken());

  return (
    <Router>
      <Toaster position="top-right" />
      <Routes>
        <Route
          path="/login"
          element={authenticated ? <Navigate to="/" /> : <Login onLogin={() => setAuthenticated(true)} />}
        />
        <Route
          path="/*"
          element={authenticated ? <AppLayout /> : <Navigate to="/login" />}
        />
      </Routes>
    </Router>
  );
}

export default App;
